import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Users, BookOpen, Check, AlertTriangle, X, ChevronDown, ChevronRight, Clock, RotateCcw } from "lucide-react";

export default function AssignTest({ workforceType = null }) {
  const nav = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [skills, setSkills] = useState([]);
  const [cmap, setCmap] = useState([]);  // [{role, skills:{code:level,...}}]
  const [selectionMode, setSelectionMode] = useState("individual");
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedDept, setSelectedDept] = useState("");
  const [skillCode, setSkillCode] = useState("");
  const [dueDate, setDueDate] = useState(() => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);   // { eligible, blocked_pending, blocked_recent }
  const [overrideSet, setOverrideSet] = useState(new Set());  // employee_ids to assign anyway
  const [showAllRoleMembers, setShowAllRoleMembers] = useState(false);  // role mode: include employees whose role-cmap doesn't contain the skill

  useEffect(() => {
    const params = { active_only: true, ...(workforceType ? { workforce_type: workforceType } : {}) };
    api.get("/employees", { params }).then((r) => setEmployees(r.data));
    api.get("/skills").then((r) => setSkills(r.data));
    api.get("/competency-map").then((r) => setCmap(r.data));
  }, [workforceType]);

  // Map: role name → set of required skill codes
  const skillsByRole = useMemo(() => {
    const m = {};
    cmap.forEach((c) => { m[c.role] = new Set(Object.keys(c.skills || {})); });
    return m;
  }, [cmap]);

  // True if `skillCode` is in the competency map of ANY of the employee's roles.
  const skillInCompetencyMap = (emp) => {
    if (!skillCode) return true;  // nothing selected yet → suppress warnings
    return emp.roles.some((r) => skillsByRole[r]?.has(skillCode));
  };

  const selectedSkillName = skills.find((s) => s.code === skillCode)?.name || skillCode;

  const roles = Array.from(new Set(employees.flatMap((e) => e.roles)));
  const depts = Array.from(new Set(employees.map((e) => e.department)));
  const deptMembers = useMemo(
    () => employees.filter((e) => e.department === selectedDept),
    [employees, selectedDept]
  );

  // For role-mode default filtering, the question is "does THIS ROLE have the
  // skill in its competency map?" — a property of the role itself, not the
  // employee. Per-row warnings still use the employee's full competency map
  // (union across all their roles).
  const selectedRoleHasSkill = !!(skillCode && selectedRole && skillsByRole[selectedRole]?.has(skillCode));

  const targets = (() => {
    if (selectionMode === "individual") return Array.from(selectedEmployees);
    if (selectionMode === "role") {
      const baseList = employees.filter((e) => e.roles.includes(selectedRole));
      // Default: only include if the selected role's cmap actually requires this skill.
      const filtered = skillCode && !showAllRoleMembers && !selectedRoleHasSkill
        ? []
        : baseList;
      return filtered.map((e) => e.employee_id);
    }
    if (selectionMode === "department") return employees.filter((e) => e.department === selectedDept).map((e) => e.employee_id);
    return [];
  })();

  // Count of role-mode members hidden by the relevance filter (used for the toggle hint).
  const hiddenRoleMembers = (selectionMode === "role" && skillCode && !showAllRoleMembers && selectedRole && !selectedRoleHasSkill)
    ? employees.filter((e) => e.roles.includes(selectedRole)).length
    : 0;

  const toggle = (id) => {
    const s = new Set(selectedEmployees);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedEmployees(s);
  };

  // Submit path: PREVIEW first → if clean, assign directly; otherwise open the modal.
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const p = await api.post("/assessments/assign/preview", {
        employee_ids: targets,
        skill_code: skillCode,
        type: "TNI",
        due_date: dueDate,
      });
      const data = p.data;
      const hasBlockers =
        data.blocked_pending.length > 0 ||
        data.blocked_recent.length > 0 ||
        (data.blocked_inactive?.length || 0) > 0;
      if (!hasBlockers) {
        await doAssign([]);
      } else {
        setPreview(data);
        setOverrideSet(new Set());
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doAssign = async (overrideRecentIds) => {
    setBusy(true); setErr("");
    try {
      const res = await api.post("/assessments/assign", {
        employee_ids: targets,
        skill_code: skillCode,
        type: "TNI",
        due_date: dueDate,
        override_recent: overrideRecentIds,
      });
      setDone(res.data);
      setPreview(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const proceedFromPreview = () => {
    doAssign(Array.from(overrideSet));
  };

  if (done) {
    return <AssignSuccessScreen done={done} skillName={skills.find((s) => s.code === skillCode)?.name} onReset={() => { setDone(null); setSelectedEmployees(new Set()); }} onView={() => nav("/my-tests")} />;
  }

  return (
    <div data-testid="assign-test-page">
      <PageHeader
        eyebrow={workforceType === "worker" ? "HR Tools · Operators" : workforceType === "staff" ? "HR Tools · Staff" : "HR Tools"}
        title={workforceType === "worker" ? "Assign Operator Test" : workforceType === "staff" ? "Assign Staff Test" : "Assign TNI Test"}
        subtitle="Create training-need identification assessments — by individual, by role, or by department."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: workforceType === "worker" ? "Assign Operator Test" : workforceType === "staff" ? "Assign Staff Test" : "Assign Test" }]}
      />
      <form onSubmit={submit} className="px-4 md:px-10 py-8 space-y-6 max-w-3xl">
        {/* Skill */}
        <div className="bc-card p-5">
          <div className="bc-label mb-3 flex items-center gap-2"><BookOpen size={12} /> Competency</div>
          <select required value={skillCode} onChange={(e) => setSkillCode(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="skill-select">
            <option value="">Select a skill…</option>
            {skills.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
          <label className="bc-label block mt-4 mb-1">Due date</label>
          <input type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="due-date-input" />
        </div>

        {/* Selection mode */}
        <div className="bc-card p-5">
          <div className="bc-label mb-3 flex items-center gap-2"><Users size={12} /> Assign to</div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {["individual", "role", "department"].map((m) => (
              <button type="button" key={m} data-testid={`mode-${m}`} onClick={() => setSelectionMode(m)} className={`text-xs font-medium py-2 border rounded-sm uppercase tracking-wider ${selectionMode === m ? "bg-[#0D0D0D] text-white border-[#0D0D0D]" : "border-[#E5E5E5] hover:border-[#0D0D0D]"}`}>
                {m}
              </button>
            ))}
          </div>

          {selectionMode === "role" && (
            <div className="space-y-3">
              <select required value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="role-select">
                <option value="">Choose a role…</option>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              {selectedRole && skillCode && hiddenRoleMembers > 0 && (
                <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800 cursor-pointer" data-testid="show-all-role-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={showAllRoleMembers}
                    onChange={(e) => setShowAllRoleMembers(e.target.checked)}
                    data-testid="show-all-role-toggle"
                    className="accent-[#C41E1E] mt-0.5"
                  />
                  <span>
                    <strong>Show all {selectedRole} employees</strong> — <strong>{selectedSkillName}</strong> is not in
                    the {selectedRole} competency map ({hiddenRoleMembers} {hiddenRoleMembers === 1 ? "employee" : "employees"} hidden).
                  </span>
                </label>
              )}
              {selectedRole && skillCode && (
                <div className="border border-[#E5E5E5] rounded-sm max-h-72 overflow-y-auto" data-testid="role-member-list">
                  {(showAllRoleMembers || selectedRoleHasSkill
                    ? employees.filter((e) => e.roles.includes(selectedRole))
                    : []
                  ).map((e) => (
                      <div key={e.employee_id} className="px-4 py-2 flex items-center justify-between border-b border-[#E5E5E5]" data-testid={`role-member-${e.employee_id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{e.name}</div>
                          <div className="text-[10px] text-[#666666] truncate">{e.department} · {e.roles.join(", ")}</div>
                          {!skillInCompetencyMap(e) && (
                            <div className="text-[10px] text-amber-800 flex items-center gap-1 mt-1" data-testid={`relevance-warning-${e.employee_id}`}>
                              <AlertTriangle size={11} /> {selectedSkillName} is not in this employee&apos;s competency map
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
          {selectionMode === "department" && (
            <div className="space-y-3">
              <select required value={selectedDept} onChange={(e) => setSelectedDept(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="dept-select">
                <option value="">Choose a department…</option>
                {depts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              {selectedDept && skillCode && (
                <div className="border border-[#E5E5E5] rounded-sm max-h-72 overflow-y-auto" data-testid="dept-member-list">
                  {deptMembers.map((e) => (
                    <div key={e.employee_id} className="px-4 py-2 flex items-center justify-between border-b border-[#E5E5E5]" data-testid={`dept-member-${e.employee_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{e.name}</div>
                        <div className="text-[10px] text-[#666666] truncate">{e.department} · {e.roles.join(", ")}</div>
                        {!skillInCompetencyMap(e) && (
                          <div className="text-[10px] text-amber-800 flex items-center gap-1 mt-1" data-testid={`relevance-warning-${e.employee_id}`}>
                            <AlertTriangle size={11} /> {selectedSkillName} is not in this employee&apos;s competency map
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {selectionMode === "individual" && (
            <div className="max-h-72 overflow-y-auto border border-[#E5E5E5] rounded-sm">
              {employees.map((e) => (
                <label key={e.employee_id} className="px-4 py-2 flex items-center justify-between border-b border-[#E5E5E5] bc-row-hover cursor-pointer">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{e.name}</div>
                    <div className="text-[10px] text-[#666666]">{e.department} · {e.roles.join(", ")}</div>
                    {!skillInCompetencyMap(e) && (
                      <div className="text-[10px] text-amber-800 flex items-center gap-1 mt-1" data-testid={`relevance-warning-${e.employee_id}`}>
                        <AlertTriangle size={11} /> {selectedSkillName} is not in this employee&apos;s competency map
                      </div>
                    )}
                  </div>
                  <input type="checkbox" checked={selectedEmployees.has(e.employee_id)} onChange={() => toggle(e.employee_id)} data-testid={`select-${e.employee_id}`} className="accent-[#C41E1E] w-4 h-4" />
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="bc-card p-5">
          <div className="bc-label">Summary</div>
          <div className="font-heading text-xl font-bold mt-1">{targets.length} employee{targets.length !== 1 ? "s" : ""} will be assigned</div>
          {skillCode && targets.length > 0 && (() => {
            const targetEmps = employees.filter((e) => targets.includes(e.employee_id));
            const offMap = targetEmps.filter((e) => !skillInCompetencyMap(e)).length;
            if (offMap === 0) return null;
            return (
              <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="relevance-summary">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <span><strong>{offMap}</strong> {offMap === 1 ? "employee" : "employees"} in this selection {offMap === 1 ? "does" : "do"} not have <strong>{selectedSkillName}</strong> in their competency map. Assigning anyway is allowed — the warning is informational only.</span>
              </div>
            );
          })()}
        </div>

        {err && <div className="text-sm text-[#C41E1E] bg-red-50 px-4 py-2 rounded-sm border border-red-200" data-testid="assign-error">{err}</div>}

        <button type="submit" disabled={busy || targets.length === 0 || !skillCode} data-testid="assign-submit" className="bg-[#C41E1E] hover:bg-[#A31818] text-white px-6 py-3 rounded-sm font-medium text-sm disabled:opacity-40">
          {busy ? "Checking…" : `Assign to ${targets.length} employee${targets.length !== 1 ? "s" : ""}`}
        </button>
      </form>

      {preview && (
        <DuplicatePreventionModal
          preview={preview}
          skillName={skills.find((s) => s.code === skillCode)?.name || skillCode}
          overrideSet={overrideSet}
          setOverrideSet={setOverrideSet}
          busy={busy}
          onCancel={() => setPreview(null)}
          onProceed={proceedFromPreview}
        />
      )}
    </div>
  );
}

function AssignSuccessScreen({ done, skillName, onReset, onView }) {
  const [showSkipped, setShowSkipped] = useState(false);
  const skippedCount = (done.skipped_pending?.length || 0) + (done.skipped_recent?.length || 0);
  return (
    <div data-testid="assign-success" className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="bc-card max-w-lg w-full p-8">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto bg-green-100 text-green-700 rounded-full flex items-center justify-center"><Check size={28} /></div>
          <h2 className="font-heading text-xl font-bold mt-4">{done.count} assessment{done.count !== 1 ? "s" : ""} created</h2>
          {skippedCount > 0 && (
            <p className="text-sm text-amber-700 mt-2" data-testid="skipped-summary">
              {skippedCount} skipped — already have pending assessments{" "}
              {done.skipped_pending?.length > 0 && (
                <>
                  (
                  {done.skipped_pending.slice(0, 2).map((s, i) => (
                    <span key={s.employee_id}>{i > 0 ? ", " : ""}{s.name} — {s.skill_name}</span>
                  ))}
                  {done.skipped_pending.length > 2 && <span>, +{done.skipped_pending.length - 2} more</span>}
                  )
                </>
              )}
              .
            </p>
          )}
        </div>
        {skippedCount > 0 && (
          <div className="mt-4 border-t border-[#E5E5E5] pt-4">
            <button
              type="button"
              onClick={() => setShowSkipped((v) => !v)}
              data-testid="view-skipped-toggle"
              className="text-xs text-[#C41E1E] hover:underline inline-flex items-center gap-1 font-medium"
            >
              {showSkipped ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              View skipped ({skippedCount})
            </button>
            {showSkipped && (
              <div className="mt-3 space-y-3 text-sm" data-testid="skipped-list">
                {done.skipped_pending?.length > 0 && (
                  <div>
                    <div className="bc-label mb-1.5 flex items-center gap-1.5"><Clock size={11} /> Pending / overdue ({done.skipped_pending.length})</div>
                    <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]">
                      {done.skipped_pending.map((s) => (
                        <div key={s.employee_id} className="px-3 py-1.5 flex justify-between items-baseline">
                          <span className="font-medium">{s.name}</span>
                          <span className="text-xs text-[#666666]">{s.skill_name} · assigned {s.assigned_date}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {done.skipped_recent?.length > 0 && (
                  <div>
                    <div className="bc-label mb-1.5 flex items-center gap-1.5"><RotateCcw size={11} /> Recently completed — not overridden ({done.skipped_recent.length})</div>
                    <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]">
                      {done.skipped_recent.map((s) => (
                        <div key={s.employee_id} className="px-3 py-1.5 flex justify-between items-baseline">
                          <span className="font-medium">{s.name}</span>
                          <span className="text-xs text-[#666666]">{s.skill_name} · {s.completed_date} · {s.result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <button onClick={onView} className="mt-6 w-full bg-[#0D0D0D] text-white text-sm py-3 rounded-sm" data-testid="view-test-register">View test register</button>
        <button onClick={onReset} className="mt-2 w-full border border-[#E5E5E5] text-sm py-3 rounded-sm" data-testid="assign-another">Assign another</button>
      </div>
    </div>
  );
}

function DuplicatePreventionModal({ preview, skillName, overrideSet, setOverrideSet, busy, onCancel, onProceed }) {
  const { eligible, blocked_pending, blocked_recent, blocked_inactive = [] } = preview;
  const allRecentSelected = blocked_recent.length > 0 && blocked_recent.every((r) => overrideSet.has(r.employee_id));

  const toggleOverride = (id) => {
    const s = new Set(overrideSet);
    s.has(id) ? s.delete(id) : s.add(id);
    setOverrideSet(s);
  };

  const toggleAllRecent = () => {
    if (allRecentSelected) {
      setOverrideSet(new Set());
    } else {
      setOverrideSet(new Set(blocked_recent.map((r) => r.employee_id)));
    }
  };

  const willCreate = eligible.length + overrideSet.size;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" data-testid="duplicate-prevention-modal">
      <div className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-amber-50 text-amber-700 rounded-sm flex items-center justify-center shrink-0"><AlertTriangle size={18} /></div>
            <div>
              <h3 className="font-heading text-base font-semibold">Duplicate-prevention check</h3>
              <div className="text-xs text-[#666666] mt-0.5">Reviewing assignments for <strong>{skillName}</strong></div>
            </div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-5 text-sm">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-sm px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-green-700">Will create</div>
              <div className="font-mono text-lg font-bold text-green-700" data-testid="willcreate-count">{willCreate}</div>
            </div>
            <div className="bg-neutral-100 border border-neutral-200 rounded-sm px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-neutral-700">Skipped (pending)</div>
              <div className="font-mono text-lg font-bold text-neutral-700" data-testid="pending-count">{blocked_pending.length}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-amber-700">Recent — needs OK</div>
              <div className="font-mono text-lg font-bold text-amber-700" data-testid="recent-count">{blocked_recent.length}</div>
            </div>
          </div>

          {/* Pending — cannot override */}
          {blocked_pending.length > 0 && (
            <div data-testid="pending-section">
              <div className="bc-label mb-2 flex items-center gap-1.5"><Clock size={11} /> Already pending — will be skipped</div>
              <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]">
                {blocked_pending.map((p) => (
                  <div key={p.employee_id} className="px-3 py-2 text-sm" data-testid={`pending-row-${p.employee_id}`}>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#666666] mt-0.5">
                      Already has a pending TNI assessment for {p.skill_name} assigned on {p.assigned_date}. Please wait
                      for them to complete it before reassigning.
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inactive — cannot override */}
          {blocked_inactive.length > 0 && (
            <div data-testid="inactive-section">
              <div className="bc-label mb-2 flex items-center gap-1.5"><X size={11} /> Inactive — will be skipped</div>
              <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]">
                {blocked_inactive.map((p) => (
                  <div key={p.employee_id} className="px-3 py-2 text-sm" data-testid={`inactive-row-${p.employee_id}`}>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#666666] mt-0.5">
                      Status is <span className="font-mono">{p.status || "inactive"}</span> — new tests can only be assigned to active employees.
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent — overridable */}
          {blocked_recent.length > 0 && (
            <div data-testid="recent-section">
              <div className="flex items-center justify-between mb-2">
                <div className="bc-label flex items-center gap-1.5"><RotateCcw size={11} /> Recently completed (last 6 months)</div>
                <button
                  type="button"
                  onClick={toggleAllRecent}
                  data-testid="toggle-all-overrides"
                  className="text-xs text-[#C41E1E] hover:underline font-medium"
                >
                  {allRecentSelected ? "Clear all" : "Override all"}
                </button>
              </div>
              <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]">
                {blocked_recent.map((r) => (
                  <label key={r.employee_id} className="px-3 py-2 flex items-start justify-between gap-3 cursor-pointer bc-row-hover" data-testid={`recent-row-${r.employee_id}`}>
                    <div>
                      <div className="font-medium text-sm">{r.name}</div>
                      <div className="text-xs text-[#666666] mt-0.5">
                        Completed a TNI assessment for {r.skill_name} on {r.completed_date} with result{" "}
                        <strong>{r.result === "pass" ? "Pass" : "Fail"}</strong>
                        {r.achieved_level && <> · achieved {r.achieved_level}</>}. Are you sure you want to assign
                        again?
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={overrideSet.has(r.employee_id)}
                      onChange={() => toggleOverride(r.employee_id)}
                      data-testid={`override-${r.employee_id}`}
                      className="accent-[#C41E1E] w-4 h-4 mt-0.5"
                    />
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-[#666666] mt-1">Tick the box to override the duplicate-prevention check and re-assign anyway.</div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#E5E5E5] flex items-center justify-between gap-3">
          <div className="text-xs text-[#666666]">
            <strong className="text-[#0D0D0D]">{willCreate}</strong> of {preview.eligible.length + preview.blocked_pending.length + preview.blocked_recent.length} will be assigned
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} disabled={busy} className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm disabled:opacity-60">Cancel</button>
            <button
              type="button"
              onClick={onProceed}
              disabled={busy || willCreate === 0}
              data-testid="proceed-assign"
              className="text-sm px-4 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Assigning…" : willCreate === 0 ? "Nothing to assign" : `Assign ${willCreate}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
